import React from 'react'
import { formatEventTimeRange, SLOT_HEIGHT_PX } from './dateUtils'
import { AvailabilityCalendarEvent } from './types'
import {
  EventBlock,
  EventSpaceLabel,
  EventTime,
  EventTitle,
} from './style'

interface CalendarEventBlockProps {
  event: AvailabilityCalendarEvent
  showSpaceLabel?: boolean
  inset?: boolean
}

const getEventLayout = (
  event: AvailabilityCalendarEvent
): { top: number; height: number; zIndex: number; durationMinutes: number } => {
  const startMin = event.start.getMinutes()
  const startTotal = event.start.getHours() * 60 + event.start.getMinutes()
  const endTotal = event.end.getHours() * 60 + event.end.getMinutes()
  const top = (startMin / 60) * SLOT_HEIGHT_PX
  const durationMinutes = Math.max(endTotal - startTotal, 15)
  const height = Math.max((durationMinutes / 60) * SLOT_HEIGHT_PX, 22)
  const zIndex = Math.max(1, 500 - durationMinutes)
  return { top, height, zIndex, durationMinutes }
}

export const sortEventsLongestFirst = (
  events: AvailabilityCalendarEvent[]
): AvailabilityCalendarEvent[] =>
  [...events].sort((a, b) => {
    const durationA = a.end.getTime() - a.start.getTime()
    const durationB = b.end.getTime() - b.start.getTime()
    if (durationB !== durationA) return durationB - durationA
    return a.start.getTime() - b.start.getTime()
  })

export const CalendarEventBlock = ({
  event,
  showSpaceLabel = true,
  inset = false,
}: CalendarEventBlockProps): JSX.Element => {
  const { top, height, zIndex, durationMinutes } = getEventLayout(event)
  const timeRange = formatEventTimeRange(event)
  const isCompact = durationMinutes <= 30
  const showTime = !isCompact && height > 28
  const showSpace = !isCompact && showSpaceLabel && height > 50

  return (
    <EventBlock
      $color={event.color}
      $top={top}
      $height={height}
      $zIndex={zIndex}
      $compact={isCompact}
      style={inset ? { left: 4, right: 4 } : undefined}
      title={`${timeRange} · ${event.title} · ${event.roomName}`}
    >
      {isCompact ? (
        <EventTitle>
          {event.title} {timeRange}
        </EventTitle>
      ) : (
        <>
          <EventTitle>{event.title}</EventTitle>
          {showTime && <EventTime>{timeRange}</EventTime>}
          {showSpace && <EventSpaceLabel>{event.roomName}</EventSpaceLabel>}
        </>
      )}
    </EventBlock>
  )
}
